import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Plus, FileText, Clock, CheckCircle, AlertCircle, 
  LogOut, User, Building2, Trash2, Eye, Download, Settings, MessageSquare, ClipboardCheck, Pencil, ArrowRight, FileCheck, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import ContactSupportModal from "../components/ContactSupportModal";
import BrandWordmark from "../components/BrandWordmark";
import ComplianceMemoModal from "../components/ComplianceMemoModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingFinalId, setDownloadingFinalId] = useState(null);
  const [memoModal, setMemoModal] = useState({ open: false, projectId: null, projectName: "", memoType: "cwmp" });

  const openMemo = (project, memoType) => {
    setMemoModal({
      open: true,
      projectId: project.project_id,
      projectName: project.project_name || project.address || "Project",
      memoType,
    });
  };
  const closeMemo = () => setMemoModal((s) => ({ ...s, open: false }));

  useEffect(() => {
    fetchProjects();
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/check-admin`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.is_admin);
      }
    } catch (error) {
      console.log('Not admin');
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/projects`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        setProjects(projects.filter(p => p.project_id !== projectId));
        toast.success('Project deleted');
      }
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleDownloadPDF = async (project) => {
    setDownloadingId(project.project_id);
    try {
      const token = localStorage.getItem('token');
      const endpoint = `${API}/generate/cwmp/${project.project_id}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CWMP_${project.project_name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('CWMP PDF downloaded successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadFinalPDF = async (project) => {
    setDownloadingFinalId(project.project_id);
    try {
      const token = localStorage.getItem('token');
      const endpoint = `${API}/generate/final-report/${project.project_id}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FinalReport_${project.project_name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Final Report PDF downloaded successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDownloadingFinalId(null);
    }
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (paymentStatus === 'paid') {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Paid</Badge>;
    }
    switch (status) {
      case 'draft':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Draft</Badge>;
      case 'pending':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Pending Payment</Badge>;
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const stats = {
    total: projects.length,
    drafts: projects.filter(p => p.status === 'draft').length,
    completed: projects.filter(p => p.payment_status === 'paid').length
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <BrandWordmark size="md" onClick={() => navigate('/')} />
            
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
                <User className="w-4 h-4" />
                <span>{user?.name || user?.email}</span>
                {user?.company && (
                  <>
                    <span className="text-primary/50">•</span>
                    <Building2 className="w-4 h-4" />
                    <span>{user.company}</span>
                  </>
                )}
              </div>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="border-primary/30 text-primary hover:bg-primary/10"
                  data-testid="admin-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-muted-foreground hover:text-foreground"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground font-['Manrope']" data-testid="dashboard-title">
            Welcome, {user?.name?.split(' ')[0] || 'User'}
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your CALGreen waste management plans
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-primary/20 bg-card/50" data-testid="stat-total">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-bold text-foreground">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Projects</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/20 bg-card/50" data-testid="stat-drafts">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-foreground">{stats.drafts}</p>
                <p className="text-sm text-muted-foreground">Drafts</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/20 bg-card/50" data-testid="stat-completed">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-foreground">{stats.completed}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Projects Section */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground font-['Manrope']">Your Projects</h2>
          <Button 
            onClick={() => navigate('/project/new')}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="new-project-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : projects.length === 0 ? (
          <Card className="border-primary/20 border-dashed" data-testid="empty-state">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No projects yet</h3>
              <p className="text-muted-foreground mb-4 text-center max-w-sm">
                Create your first CALGreen-compliant waste management plan in minutes.
              </p>
              <Button 
                onClick={() => navigate('/project/new')}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="empty-new-project-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Plan
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {projects.map((project) => (
              <div 
                key={project.project_id}
                className="space-y-3"
                data-testid={`project-card-${project.project_id}`}
              >
                {/* Project Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-foreground">
                      {project.project_name}
                    </h3>
                    <Badge variant="outline" className="text-xs">
                      {project.building_type?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteProject(project.project_id)}
                    className="text-destructive hover:bg-destructive/10"
                    data-testid={`delete-project-${project.project_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Project Info */}
                <p className="text-sm text-muted-foreground">
                  {project.address && `${project.address}, `}{project.city}, {project.county} • {project.square_footage?.toLocaleString()} sq ft
                </p>

                {/* Two Card Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Pre-Construction CWMP Card */}
                  <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-primary" />
                          <span className="font-semibold text-foreground">Pre-Construction</span>
                        </div>
                        {project.payment_status === 'paid' ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Complete
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                            <Clock className="w-3 h-3 mr-1" />
                            Draft
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-4">
                        Construction Waste Management Plan (CWMP)
                      </p>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/project/${project.project_id}`)}
                          className="border-primary/50 text-foreground hover:bg-primary/10"
                          data-testid={`view-cwmp-${project.project_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/project/${project.project_id}/edit`)}
                          className="border-primary/50 text-foreground hover:bg-primary/10"
                          data-testid={`edit-cwmp-${project.project_id}`}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        {(project.payment_status === 'paid' || isAdmin) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadPDF(project)}
                            disabled={downloadingId === project.project_id}
                            className="border-primary/50 text-foreground hover:bg-primary/10"
                            data-testid={`download-cwmp-${project.project_id}`}
                          >
                            {downloadingId === project.project_id ? (
                              <>
                                <svg className="animate-spin w-4 h-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                ...
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-1" />
                                PDF
                              </>
                            )}
                          </Button>
                        )}
                        {(project.payment_status === 'paid' || isAdmin) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openMemo(project, "cwmp")}
                            className="border-accent/50 text-accent hover:bg-accent/10"
                            data-testid={`memo-cwmp-${project.project_id}`}
                            title="AI Compliance Memo"
                          >
                            <Sparkles className="w-4 h-4 mr-1" />
                            Memo
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Arrow Connector (hidden on mobile) */}
                  <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    {/* This would need positioning adjustment */}
                  </div>

                  {/* Post-Construction Final Report Card */}
                  <Card className={`border-primary/20 transition-colors ${
                    project.payment_status === 'paid' || isAdmin 
                      ? 'hover:border-blue-500/40' 
                      : 'opacity-60'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <FileCheck className="w-5 h-5 text-blue-400" />
                          <span className="font-semibold text-foreground">Post-Construction</span>
                        </div>
                        {project.final_report_status === 'complete' ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Complete
                          </Badge>
                        ) : project.final_report_status === 'ready' ? (
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                            <Clock className="w-3 h-3 mr-1" />
                            Ready
                          </Badge>
                        ) : project.payment_status === 'paid' || isAdmin ? (
                          <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30">
                            Locked
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-4">
                        Final Waste Verification Report
                      </p>
                      
                      {project.payment_status === 'paid' || isAdmin ? (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/project/${project.project_id}/final-report`)}
                            className="border-primary/50 text-foreground hover:bg-primary/10"
                            data-testid={`view-final-${project.project_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {project.final_report_status ? 'View' : 'Start'}
                          </Button>
                          {project.final_report_status && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/project/${project.project_id}/final-report`)}
                                className="border-primary/50 text-foreground hover:bg-primary/10"
                                data-testid={`edit-final-${project.project_id}`}
                              >
                                <Pencil className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadFinalPDF(project)}
                                disabled={downloadingFinalId === project.project_id}
                                className="border-primary/50 text-foreground hover:bg-primary/10"
                                data-testid={`download-final-${project.project_id}`}
                              >
                                {downloadingFinalId === project.project_id ? (
                                  <>
                                    <svg className="animate-spin w-4 h-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    ...
                                  </>
                                ) : (
                                  <>
                                    <Download className="w-4 h-4 mr-1" />
                                    PDF
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openMemo(project, "final")}
                                className="border-accent/50 text-accent hover:bg-accent/10"
                                data-testid={`memo-final-${project.project_id}`}
                                title="AI Verification Memo"
                              >
                                <Sparkles className="w-4 h-4 mr-1" />
                                Memo
                              </Button>
                            </>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">
                          Complete CWMP payment to unlock
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Divider between projects */}
                <div className="border-b border-primary/10 pt-2" />
              </div>
            ))}
          </div>
        )}

        {/* Contact Support Button - positioned above the Emergent badge */}
        <div className="fixed bottom-20 right-6 z-40">
          <Button
            onClick={() => setShowContactModal(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg rounded-full px-4 py-6"
            data-testid="contact-support-btn"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Support
          </Button>
        </div>

        {/* Contact Support Modal */}
        <ContactSupportModal 
          isOpen={showContactModal} 
          onClose={() => setShowContactModal(false)} 
        />

        {/* AI Compliance Memo Modal */}
        <ComplianceMemoModal
          isOpen={memoModal.open}
          onClose={closeMemo}
          projectId={memoModal.projectId}
          projectName={memoModal.projectName}
          memoType={memoModal.memoType}
        />
      </main>
    </div>
  );
};

export default Dashboard;
